import { normalize_text, to_array, to_number } from './view_case_details_core_helpers';
import {
  field_value,
  first_present,
  normalize_label,
  risk_choice_label,
  risk_choice_weight,
} from './view_case_details_risk_choice_helpers';

const RISK_SCORE_FIELDS = ['total_score', 'score', 'risk_score'];

export function clamp_risk_score(value) {
  const score = Number(value);
  return Number.isFinite(score) ? Math.max(0, Math.min(100, Math.round(score))) : 0;
}

export function answer_selections(value) {
  return Array.isArray(value) ? value.map(normalize_text).filter(Boolean) : [normalize_text(value)].filter(Boolean);
}

function matching_choice(question = {}, answer = '') {
  return to_array(question?.choices).find((item) =>
    normalize_label(risk_choice_label(item)) === normalize_label(answer)
  ) || {};
}

function risk_driver(question_text, answer, choice = {}) {
  const weight = risk_choice_weight(choice);
  const signed_weight = weight > 0 ? `+${weight}` : String(weight);

  return {
    delta: Number.isFinite(weight) ? signed_weight : '0',
    label: `${question_text}: ${answer}`,
    question: question_text,
    answer,
    weight: Number.isFinite(weight) ? signed_weight : '0',
    tag: 'Risk',
    direction: weight < 0 ? 'down' : 'up',
    sort_weight: Math.abs(weight),
  };
}

function question_text(question = {}, question_id = '') {
  return normalize_text(question?.question_text || question?.text || `Question ${question_id}`);
}

function drivers_for_answer(question = {}, question_id = '', raw_answer = '') {
  const label = question_text(question, question_id);
  return answer_selections(raw_answer).map((answer) =>
    risk_driver(label, answer, matching_choice(question, answer))
  );
}

function remove_sort_weight(driver = {}) {
  const normalized_driver = { ...driver };
  delete normalized_driver.sort_weight;
  return normalized_driver;
}

function question_by_id(questions = [], question_id = '') {
  return questions.find((item) => Number(item?.id) === Number(question_id)) || {};
}

export function normalize_risk_drivers(result = {}, assessment = {}) {
  const questions = to_array(assessment?.questions);
  const drivers = Object.entries(result?.answers || {}).flatMap(([question_id, raw_answer]) =>
    drivers_for_answer(question_by_id(questions, question_id), question_id, raw_answer)
  );

  return drivers
    .sort((left, right) => Number(right.sort_weight || 0) - Number(left.sort_weight || 0))
    .map(remove_sort_weight);
}

function has_risk_panel_data(raw_score, raw_band, drivers) {
  return raw_score !== undefined || Boolean(raw_band) || Boolean(drivers.length);
}

function risk_score_value(result = {}) {
  return first_present(RISK_SCORE_FIELDS.map((field) => field_value(result, field)));
}

function risk_panel_payload(raw_score, raw_band, drivers, assessment = {}) {
  return {
    score: clamp_risk_score(raw_score),
    band: raw_band,
    assessment_id: to_number(assessment?.id),
    assessment_name: normalize_text(assessment?.name || assessment?.risk_assessment),
    drivers,
  };
}

export function normalize_risk_panel(result = {}, assessment = {}) {
  if (!result || typeof result !== 'object') return null;

  const raw_score = risk_score_value(result);
  const raw_band = normalize_text(result?.risk_level || result?.risk_band);
  const drivers = normalize_risk_drivers(result, assessment);

  return has_risk_panel_data(raw_score, raw_band, drivers)
    ? risk_panel_payload(raw_score, raw_band, drivers, assessment)
    : null;
}
